import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getCandidatures, bulkUpdateStatut, exportUrl, exportXlsxUrl, documentsLotUrl } from '../../services/api';
import { STATUTS, CATEGORIES_SCORE } from '../../constants/formOptions';
import usePolling from '../../hooks/usePolling';
import Live from './Live';

const PAGE_SIZE = 20;
const FILTER_KEYS = ['search', 'statut', 'sexe', 'categorie_score'];

export const StatusBadge = ({ statut }) => {
  const s = STATUTS.find(st => st.value === statut);
  if (!s) return <span className="status-badge">{statut}</span>;
  return <span className="status-badge" style={{ background: `${s.color}1a`, color: s.color }}>{s.label}</span>;
};

export const ScorePill = ({ score, categorie }) => {
  const color = (CATEGORIES_SCORE.find(c => c.value === categorie) || {}).color || '#6b7280';
  return <span className="score-pill" style={{ background: color }}>{score}</span>;
};

const shortLabel = (label = '') => label.split(' (')[0];

const formatDate = (iso) => new Date(iso).toLocaleString('fr-FR', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
});

const ApplicationsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = Object.fromEntries(FILTER_KEYS.map(k => [k, searchParams.get(k) || '']));
  const page = parseInt(searchParams.get('page') || '1', 10);
  const queryKey = searchParams.toString();

  const [search, setSearch] = useState(filters.search);
  const [candidatures, setCandidatures] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [newIds, setNewIds] = useState(new Set());
  const knownIds = useRef(null);

  const updateParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    if (!('page' in updates)) next.delete('page');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => setSearch(filters.search), [filters.search]);

  // Recherche : applique la saisie après une courte pause
  useEffect(() => {
    if (search === filters.search) return undefined;
    const t = setTimeout(() => updateParams({ search: search.trim() }), 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  // Nouveau jeu de filtres : on repart de zéro
  useEffect(() => {
    setLoading(true);
    setSelected([]);
    knownIds.current = null;
  }, [queryKey]);

  const lastUpdate = usePolling(async () => {
    const data = await getCandidatures({ ...filters, page });
    const results = data.results || [];
    if (knownIds.current) {
      const fresh = results.filter(c => !knownIds.current.has(c.id)).map(c => c.id);
      setNewIds(new Set(fresh));
    }
    knownIds.current = new Set(results.map(c => c.id));
    setCandidatures(results);
    setTotalCount(data.count || 0);
    setLoading(false);
  }, undefined, [queryKey]);

  const refresh = async () => {
    const data = await getCandidatures({ ...filters, page });
    setCandidatures(data.results || []);
    setTotalCount(data.count || 0);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const allSelected = candidatures.length > 0 && candidatures.every(c => selected.includes(c.id));

  const toggleAll = () => setSelected(allSelected ? [] : candidatures.map(c => c.id));
  const toggleOne = (id) => setSelected(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]));

  const handleBulk = async (statut) => {
    try {
      await bulkUpdateStatut(selected, statut);
      toast.success(`${selected.length} candidature(s) mise(s) à jour.`);
      setSelected([]);
      refresh();
    } catch {
      toast.error('La mise à jour a échoué.');
    }
  };

  const exportFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Candidatures<span className="count">{totalCount}</span></h1>
          <Live lastUpdate={lastUpdate} />
        </div>
        <div className="actions">
          <a className="btn btn-primary btn-sm" href={exportXlsxUrl({})}>Exporter tout (Excel)</a>
          <a className="btn btn-secondary btn-sm" href={exportUrl(exportFilters)}>Exporter (CSV)</a>
          <a className="btn btn-secondary btn-sm" href={documentsLotUrl(exportFilters)}>Dossiers (ZIP)</a>
        </div>
      </div>

      <div className="table-container">
        <div className="table-filters">
          <input
            type="search"
            placeholder="Nom, n° de dossier, téléphone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={filters.statut} onChange={(e) => updateParams({ statut: e.target.value })}>
            <option value="">Tous les statuts</option>
            {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={filters.categorie_score} onChange={(e) => updateParams({ categorie_score: e.target.value })}>
            <option value="">Tous les scores</option>
            {CATEGORIES_SCORE.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select value={filters.sexe} onChange={(e) => updateParams({ sexe: e.target.value })}>
            <option value="">Tous les sexes</option>
            <option value="F">Femmes</option>
            <option value="M">Hommes</option>
          </select>
        </div>

        {selected.length > 0 && (
          <div className="bulk-bar">
            <strong>{selected.length} sélectionnée(s)</strong>
            <button className="btn btn-primary btn-sm" onClick={() => handleBulk('preselectionne')}>Présélectionner</button>
            <button className="btn btn-success btn-sm" onClick={() => handleBulk('retenu')}>Retenir</button>
            <button className="btn btn-danger btn-sm" onClick={() => handleBulk('rejete')}>Rejeter</button>
            <a className="btn btn-secondary btn-sm" href={documentsLotUrl({ ids: selected.join(',') })}>Dossiers (ZIP)</a>
            <button className="btn btn-ghost btn-sm" onClick={() => setSelected([])}>Annuler</button>
          </div>
        )}

        {loading ? (
          <div className="loader"><div className="spinner" /></div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input type="checkbox" onChange={toggleAll} checked={allSelected} aria-label="Tout sélectionner" />
                  </th>
                  <th>Dossier</th>
                  <th>Candidat</th>
                  <th>Téléphones</th>
                  <th>Diplôme</th>
                  <th>Expérience</th>
                  <th>Score</th>
                  <th>Statut</th>
                  <th>Reçue le</th>
                </tr>
              </thead>
              <tbody>
                {candidatures.length === 0 ? (
                  <tr><td colSpan="9" className="empty">Aucune candidature.</td></tr>
                ) : candidatures.map(c => (
                  <tr
                    key={c.id}
                    className={`clickable ${newIds.has(c.id) ? 'row-new' : ''}`}
                    onClick={() => navigate(`/recruteur/list/${c.id}`)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleOne(c.id)} />
                    </td>
                    <td className="mono">{c.numero_dossier}</td>
                    <td>
                      <div>{c.nom} {c.prenoms}</div>
                      {c.email && <div className="muted" style={{ fontSize: '0.78rem' }}>{c.email}</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div>{c.telephone}</div>
                      {c.telephone_2 && <div className="muted" style={{ fontSize: '0.78rem' }}>{c.telephone_2}</div>}
                    </td>
                    <td>
                      {c.domaine_formation_display || '—'}
                      <div className="muted" style={{ fontSize: '0.78rem' }}>{shortLabel(c.niveau_etude_display)}</div>
                    </td>
                    <td>
                      {c.annees_experience} an{c.annees_experience > 1 ? 's' : ''}
                      {c.experience_enquetes_sante && <div className="muted" style={{ fontSize: '0.78rem' }}>santé / vaccination</div>}
                    </td>
                    <td><ScorePill score={c.score} categorie={c.categorie_score} /></td>
                    <td><StatusBadge statut={c.statut} /></td>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(c.date_soumission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => updateParams({ page: String(page - 1) })}>←</button>
            <span className="muted" style={{ fontSize: '0.8rem', alignSelf: 'center', padding: '0 0.5rem' }}>
              {page} / {totalPages}
            </span>
            <button disabled={page >= totalPages} onClick={() => updateParams({ page: String(page + 1) })}>→</button>
          </div>
        )}
      </div>
    </>
  );
};

export default ApplicationsList;
